import ROUTER from '../../routerMap/ROUTER.ts';

export type RequestTypeKey =
  | 'TEST_MATERIAL'
  | 'TEST_PQR'
  | 'TEST_PRODUCTION'
  | 'TEST_BOLTS'
  | 'CALIBRATION_MEASURING_EQUIPMENT'
  | 'CALIBRATION_PSV'
  | 'CALIBRATION_WELDING_MACHINE'
  | 'CALIBRATION_WIRE_FEEDER'
  | 'INSPECTION';

/** Mã số nội bộ dùng cho prop `type` của các màn RequestManagement. */
export type RequestTypeCode = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export type AgentValue = 'VT' | 'BH';

/** Giá trị bộ lọc Agent trên trang gộp: `ALL` = mọi địa điểm được phép truy cập. */
export type AgentFilterValue = AgentValue | 'ALL';

export type ServiceGroup = 'TESTING' | 'CALIBRATION' | 'INSPECTION';

export const SERVICE_GROUPS: readonly ServiceGroup[] = [
  'TESTING',
  'CALIBRATION',
  'INSPECTION',
];

export const isServiceGroup = (value: unknown): value is ServiceGroup =>
  SERVICE_GROUPS.includes(value as ServiceGroup);

export const SERVICE_GROUP_LABEL: Record<ServiceGroup, string> = {
  TESTING: 'Testing',
  CALIBRATION: 'Calibration',
  INSPECTION: 'Request Inspection Management',
};

export type RequestItemListField =
  | 'requestTestMaterialList'
  | 'requestTestPQRList'
  | 'requestTestProductionList'
  | 'requestTestBoltsList'
  | 'requestCalibrationEquipmentList'
  | 'requestCalibrationPSVList'
  | 'requestCalibrationWeldingMachineList'
  | 'requestCalibrationWireFeederList'
  | 'requestInspectionList';

export interface RequestTypeConfig {
  code: RequestTypeCode;
  key: RequestTypeKey;
  /** Giữ tương thích với `CONSTANT_REQUEST_TYPE` và `getLabelFromConstant`. */
  value: RequestTypeKey;
  /** Nhãn hiển thị trong select/radio, ví dụ "Request Test Material". */
  label: string;
  /** Tiêu đề trang danh sách/chi tiết. */
  pageTitle: string;
  service: ServiceGroup;
  /** Tên field danh sách item trong payload gửi backend. */
  itemListField: RequestItemListField;
  /** Chỉ dùng ánh xạ URL legacy. Loại mới không cần khai báo route riêng. */
  listPaths?: Record<AgentValue, string>;
}

export const AGENT_VALUES: readonly AgentValue[] = ['VT', 'BH'];

export const REQUEST_TYPE_BY_KEY: Record<RequestTypeKey, RequestTypeConfig> = {
  TEST_MATERIAL: {
    code: 1,
    key: 'TEST_MATERIAL',
    value: 'TEST_MATERIAL',
    label: 'Request Test Material',
    pageTitle: 'Request Material Management',
    service: 'TESTING',
    itemListField: 'requestTestMaterialList',
    listPaths: {
      VT: ROUTER.TEST_MATERIAL,
      BH: ROUTER.TEST_MATERIAL_BIEN_HOA,
    },
  },
  TEST_PQR: {
    code: 2,
    key: 'TEST_PQR',
    value: 'TEST_PQR',
    label: 'Request Test PQR',
    pageTitle: 'Request PQR Management',
    service: 'TESTING',
    itemListField: 'requestTestPQRList',
    listPaths: {
      VT: ROUTER.TEST_PQR,
      BH: ROUTER.TEST_PQR_BIEN_HOA,
    },
  },
  TEST_PRODUCTION: {
    code: 3,
    key: 'TEST_PRODUCTION',
    value: 'TEST_PRODUCTION',
    label: 'Request Test Production',
    pageTitle: 'Request Production Management',
    service: 'TESTING',
    itemListField: 'requestTestProductionList',
    listPaths: {
      VT: ROUTER.TEST_PRODUCTION,
      BH: ROUTER.TEST_PRODUCTION_BIEN_HOA,
    },
  },
  TEST_BOLTS: {
    code: 9,
    key: 'TEST_BOLTS',
    value: 'TEST_BOLTS',
    label: 'Request Test Bolts',
    pageTitle: 'Request Bolts Management',
    service: 'TESTING',
    itemListField: 'requestTestBoltsList',
    listPaths: {
      VT: ROUTER.TEST_BOLTS,
      BH: ROUTER.TEST_BOLTS_BIEN_HOA,
    },
  },
  CALIBRATION_MEASURING_EQUIPMENT: {
    code: 4,
    key: 'CALIBRATION_MEASURING_EQUIPMENT',
    value: 'CALIBRATION_MEASURING_EQUIPMENT',
    label: 'Calibration Measuring Equipment',
    pageTitle: 'Calibration Equipment Management',
    service: 'CALIBRATION',
    itemListField: 'requestCalibrationEquipmentList',
    listPaths: {
      VT: ROUTER.CALIBRATION_MEASURING_EQUIPMENT,
      BH: ROUTER.CALIBRATION_MEASURING_EQUIPMENT_BIEN_HOA,
    },
  },
  CALIBRATION_PSV: {
    code: 5,
    key: 'CALIBRATION_PSV',
    value: 'CALIBRATION_PSV',
    label: 'Calibration PSV',
    pageTitle: 'Calibration PSV Management',
    service: 'CALIBRATION',
    itemListField: 'requestCalibrationPSVList',
    listPaths: {
      VT: ROUTER.CALIBRATION_PSV,
      BH: ROUTER.CALIBRATION_PSV_BIEN_HOA,
    },
  },
  CALIBRATION_WELDING_MACHINE: {
    code: 6,
    key: 'CALIBRATION_WELDING_MACHINE',
    value: 'CALIBRATION_WELDING_MACHINE',
    label: 'Calibration Welding Machine',
    pageTitle: 'Calibration Welding Machine Management',
    service: 'CALIBRATION',
    itemListField: 'requestCalibrationWeldingMachineList',
    listPaths: {
      VT: ROUTER.CALIBRATION_WELDING_MACHINE,
      BH: ROUTER.CALIBRATION_WELDING_MACHINE_BIEN_HOA,
    },
  },
  CALIBRATION_WIRE_FEEDER: {
    code: 7,
    key: 'CALIBRATION_WIRE_FEEDER',
    value: 'CALIBRATION_WIRE_FEEDER',
    label: 'Calibration Wire Feeder',
    pageTitle: 'Calibration Wire Feeder Management',
    service: 'CALIBRATION',
    itemListField: 'requestCalibrationWireFeederList',
    listPaths: {
      VT: ROUTER.CALIBRATION_WIRE_FEEDER,
      BH: ROUTER.CALIBRATION_WIRE_FEEDER_BIEN_HOA,
    },
  },
  INSPECTION: {
    code: 8,
    key: 'INSPECTION',
    value: 'INSPECTION',
    label: 'Request Inspection',
    pageTitle: 'Request Inspection Management',
    service: 'INSPECTION',
    itemListField: 'requestInspectionList',
    listPaths: {
      VT: ROUTER.INSPECTION,
      BH: ROUTER.INSPECTION,
    },
  },
};

export const REQUEST_TYPES: readonly RequestTypeConfig[] =
  Object.values(REQUEST_TYPE_BY_KEY);

export const isRequestTypeKey = (value: unknown): value is RequestTypeKey =>
  typeof value === 'string' &&
  Object.prototype.hasOwnProperty.call(REQUEST_TYPE_BY_KEY, value);

export const isAgentValue = (value: unknown): value is AgentValue =>
  value === 'VT' || value === 'BH';

export const isAgentFilterValue = (value: unknown): value is AgentFilterValue =>
  value === 'ALL' || isAgentValue(value);

export const getRequestTypeByCode = (
  code: RequestTypeCode,
): RequestTypeConfig => {
  const config = REQUEST_TYPES.find(item => item.code === code);
  if (!config) throw new Error(`Unknown request type code: ${code}`);
  return config;
};

export const getRequestTypeByKey = (key: RequestTypeKey): RequestTypeConfig =>
  REQUEST_TYPE_BY_KEY[key];

const requestUrl = (
  path: string,
  values: Record<string, string | undefined>,
) => {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined) params.set(key, value);
  });
  return params.size ? `${path}?${params}` : path;
};

export const isRequestId = (id: unknown): boolean =>
  (typeof id === 'string' || typeof id === 'number') &&
  /^[1-9]\d*$/.test(String(id)) &&
  Number(id) <= 2147483647;

const requestId = (id: string | number): string => {
  if (!isRequestId(id)) throw new Error('ID yêu cầu không hợp lệ.');
  return String(id);
};

export const buildRequestListUrl = (
  key?: RequestTypeKey,
  agent?: AgentFilterValue,
  service?: ServiceGroup,
): string => {
  if (key !== undefined && !isRequestTypeKey(key))
    throw new Error('Loại yêu cầu không hợp lệ.');
  if (agent !== undefined && !isAgentFilterValue(agent))
    throw new Error('Agent không hợp lệ.');
  if (service !== undefined && !isServiceGroup(service))
    throw new Error('Nhóm yêu cầu không hợp lệ.');
  if (
    key !== undefined &&
    service !== undefined &&
    REQUEST_TYPE_BY_KEY[key].service !== service
  )
    throw new Error('Loại yêu cầu không thuộc nhóm đã chọn.');
  return requestUrl(ROUTER.REQUEST_MANAGEMENT, {
    requestType: key,
    agent,
    service,
  });
};

export const buildCreateRequestUrl = (
  key: RequestTypeKey,
  agent: AgentValue,
): string => {
  if (!isRequestTypeKey(key) || !isAgentValue(agent))
    throw new Error('Loại yêu cầu hoặc Agent không hợp lệ.');
  return requestUrl(ROUTER.REQUEST_MANAGEMENT_CREATE, {
    requestType: key,
    agent,
  });
};

export const buildMergedRequestListUrl = (
  agent: AgentValue,
  service?: ServiceGroup,
): string => buildRequestListUrl(undefined, agent, service);

export const buildRequestDetailUrl = (
  id: string | number,
  activeTab?: string,
): string =>
  requestUrl(ROUTER.REQUEST_MANAGEMENT_DETAIL, {
    id: requestId(id),
    activeTab,
  });

export const buildRequestUpdateUrl = (
  id: string | number,
  activeTab?: string,
): string =>
  requestUrl(ROUTER.REQUEST_MANAGEMENT_UPDATE, {
    id: requestId(id),
    activeTab,
  });

export const buildRequestCreateTabUrl = (id: string | number): string =>
  requestUrl(ROUTER.REQUEST_MANAGEMENT_CREATE_TAB, { id: requestId(id) });

/** Return to the shared service-group list the request was created from. */
export const getCreateReturnPath = (
  key: RequestTypeKey,
  agent: AgentValue,
): string =>
  buildRequestListUrl(undefined, agent, REQUEST_TYPE_BY_KEY[key].service);
